/**
 * HTTP 内核 —— 与网关通信的唯一入口(契约 §4：网关是唯一的门)。
 *
 * 这一层把契约里那些「错了不会当场崩、只会静默走错分支」的规则收拢到一处：
 *   - `Result` 壳拆包、**按 `code` 分支**(不按 HTTP 状态码) ........ 契约 §2 §3
 *   - `x-trace-id` 从**响应头**读 ................................. 契约 §8.4
 *   - 退避只对 `retryable=true`，写操作没有幂等键就不重试 ......... 契约 §2.1
 *   - 401 交给上层的单飞刷新处理 ................................. 契约 §8.4
 *
 * ⚠ **不加 `credentials: 'include'`** —— 后端 `allow-credentials: false`，
 *   加了会被浏览器直接拒。这条已写成 lint 错误。
 */
import type { z } from 'zod'
import { resultEnvelopeSchema, shouldRetry, type ResultEnvelope } from '@cione/contracts'
import {
  ApiError,
  CLIENT_ABORTED,
  CLIENT_CONTRACT_DRIFT,
  CLIENT_NETWORK,
  CLIENT_TIMEOUT,
} from './errors'
import type { TokenStore } from './token-store'

/**
 * ⚠ 一律读**响应头**，不要只从响应体拿。
 *
 * 跨域下浏览器默认只给 JS 看七个「安全头」，别的头收到了也不给读 ——
 * 后端已把它加进 `Access-Control-Expose-Headers`；不暴露的话
 * `headers.get('x-trace-id')` 恒为 null，而且**没有任何报错**。
 */
export const TRACE_ID_HEADER = 'x-trace-id'

/** 写操作要重试就必须带上它，且重试时必须是同一个值。 */
export const IDEMPOTENCY_KEY_HEADER = 'x-idempotency-key'

/**
 * 撞到这些码才值得刷新令牌。
 *
 * ⚠ `AUTH.TOKEN_INVALID` 与 `AUTH.LOGIN_REQUIRED` **不在**里面：
 *   前者是令牌本身不对(刷新救不了)，后者是压根没登录 —— 刷一次只是白跑一次往返。
 */
const REFRESHABLE_CODES: ReadonlySet<string> = new Set([
  'AUTH.TOKEN_EXPIRED',
  'COMMON.UNAUTHENTICATED',
])

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

/** `none` = 网关白名单端点(A1/A2/A4/A5)；`optional` = 有就带上，见 A3 的合并影子用户。 */
export type AuthMode = 'required' | 'optional' | 'none'

export interface RequestSpec<S extends z.ZodType> {
  method: HttpMethod
  path: string
  query?: Record<string, string | number | boolean | undefined> | undefined
  body?: unknown
  /** `data` 的 schema。契约 §2：失败时 data 恒为 null，所以只在成功时校验。 */
  schema: S
  auth?: AuthMode | undefined
  signal?: AbortSignal | undefined
  /** ⚠ 不给幂等键的写操作**不会重试** —— 请求可能已经到了服务端。 */
  idempotencyKey?: string | undefined
  /**
   * 401 时要不要触发单飞刷新。默认 true。
   *
   * ⚠ **刷新请求自己必须置 false**：A5 若回 401 就会再触发一次刷新，
   *   而单飞的 promise 正在等它自己 —— 那是一个**永远不 resolve 的死锁**，
   *   表现为整个应用的请求全部挂起，且没有任何报错。
   */
  allowRefresh?: boolean | undefined
  headers?: Record<string, string> | undefined
}

export interface RetryPolicy {
  maxAttempts: number
  baseDelayMs: number
  maxDelayMs: number
}

const DEFAULT_RETRY: RetryPolicy = { maxAttempts: 3, baseDelayMs: 300, maxDelayMs: 4000 }
const DEFAULT_TIMEOUT_MS = 15_000

export interface HttpClientOptions {
  baseUrl: string
  tokenStore: TokenStore
  fetch?: typeof globalThis.fetch | undefined
  timeoutMs?: number | undefined
  retry?: Partial<RetryPolicy> | undefined
  /**
   * 撞到 401 时的回调，返回 true 表示「已经换到新令牌，可以原样重放一次」。
   *
   * 单飞刷新挂在这里(见 refresh.ts) —— HTTP 内核本身不认识 refresh 端点，
   * 这样刷新逻辑可以单独测，而内核不必知道认证域的存在。
   */
  onUnauthorized?:
    ((error: ApiError, accessTokenUsed: string | null) => Promise<boolean>) | undefined
  sleep?: ((ms: number) => Promise<void>) | undefined
  random?: (() => number) | undefined
}

export interface HttpClient {
  readonly baseUrl: string
  readonly tokenStore: TokenStore
  readonly fetchImpl: typeof globalThis.fetch
  request<S extends z.ZodType>(spec: RequestSpec<S>): Promise<z.output<S>>
  /** 供 SSE 层复用同一套令牌规则。 */
  authHeaders(mode: AuthMode): Record<string, string>
  buildUrl(path: string, query?: Record<string, string | number | boolean | undefined>): string
}

export function createHttpClient(options: HttpClientOptions): HttpClient {
  // ⚠⚠ 不给 fetch 时**延迟到调用时**再取 globalThis.fetch，不要在这里 bind。
  //   构造时 bind 会把当时那一份 fetch 固化下来，于是任何**之后**安装的拦截
  //   (测试里的 MSW、埋点、性能探针)全部失效 —— 而失效的表现极具欺骗性：
  //   请求照样成功，只是绕过了 mock 打到了真后端。2026-09-01 实际踩到过一次。
  const fetchImpl: typeof globalThis.fetch = options.fetch
    ? options.fetch
    : (input, init) => globalThis.fetch(input, init)
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS
  const retry: RetryPolicy = { ...DEFAULT_RETRY, ...options.retry }
  const sleep = options.sleep ?? defaultSleep
  const random = options.random ?? Math.random
  const baseUrl = options.baseUrl.replace(/\/+$/, '')

  function buildUrl(
    path: string,
    query?: Record<string, string | number | boolean | undefined>,
  ): string {
    const url = new URL(`${baseUrl}${path.startsWith('/') ? path : `/${path}`}`)
    for (const [key, value] of Object.entries(query ?? {})) {
      // ⚠ 个人信息不进 query string —— 这里只允许放筛选类参数
      if (value !== undefined) url.searchParams.set(key, String(value))
    }
    return url.toString()
  }

  function authHeaders(mode: AuthMode): Record<string, string> {
    if (mode === 'none') return {}
    const token = options.tokenStore.get()?.accessToken
    if (!token) return {}
    return { Authorization: `Bearer ${token}` }
  }

  async function request<S extends z.ZodType>(spec: RequestSpec<S>): Promise<z.output<S>> {
    const authMode: AuthMode = spec.auth ?? 'required'
    const url = buildUrl(spec.path, spec.query)
    let attempt = 0
    let refreshAttempted = false

    for (;;) {
      attempt += 1
      const tokenUsed = options.tokenStore.get()?.accessToken ?? null
      const link = linkAbort(spec.signal, timeoutMs)

      let response: Response
      try {
        response = await fetchImpl(url, {
          method: spec.method,
          signal: link.signal,
          headers: {
            Accept: 'application/json',
            ...(spec.body === undefined ? {} : { 'Content-Type': 'application/json' }),
            ...(spec.idempotencyKey ? { [IDEMPOTENCY_KEY_HEADER]: spec.idempotencyKey } : {}),
            ...authHeaders(authMode),
            ...spec.headers,
          },
          ...(spec.body === undefined ? {} : { body: JSON.stringify(spec.body) }),
        })
      } catch (cause) {
        const transportError = toTransportError(cause, spec.signal, link.timedOut())
        if (canRetry(spec, transportError, attempt, retry)) {
          await sleep(backoffMs(attempt, retry, random))
          continue
        }
        throw transportError
      } finally {
        link.dispose()
      }

      // ⚠ traceId 一律优先取响应头：网关 502 与 SSE 根本没有我们的响应体
      const headerTraceId = response.headers.get(TRACE_ID_HEADER) ?? ''
      const raw = await response.text()
      const envelope = parseEnvelope(raw)

      if (!envelope) {
        // 响应体不是我们的 JSON —— 典型是网关级 502 / 反代的 HTML 错误页
        const gatewayError = new ApiError({
          code: 'COMMON.INTERNAL',
          message: `网关或上游返回了非契约响应(HTTP ${String(response.status)})`,
          traceId: headerTraceId,
          // ⚠ 只有安全方法才敢在这里认「可重试」：写操作的结果未知，
          //   重发可能造成重复副作用(同 TRADE.RESULT_UNKNOWN 的纪律)
          retryable: spec.method === 'GET' && response.status >= 502 && response.status <= 504,
          httpStatus: response.status,
        })
        if (canRetry(spec, gatewayError, attempt, retry)) {
          await sleep(backoffMs(attempt, retry, random))
          continue
        }
        throw gatewayError
      }

      const traceId = headerTraceId || envelope.traceId

      if (!envelope.success) {
        const error = new ApiError({
          code: envelope.code,
          message: envelope.message ?? envelope.code,
          traceId,
          retryable: envelope.retryable,
          fieldErrors: envelope.fieldErrors,
          httpStatus: response.status,
        })

        // 令牌过期：交给单飞刷新，成功就原样重放一次(不计入退避次数)
        if (
          response.status === 401 &&
          REFRESHABLE_CODES.has(error.code) &&
          spec.allowRefresh !== false &&
          !refreshAttempted &&
          options.onUnauthorized
        ) {
          refreshAttempted = true
          const refreshed = await options.onUnauthorized(error, tokenUsed)
          if (refreshed) {
            attempt -= 1
            continue
          }
          throw error
        }

        if (canRetry(spec, error, attempt, retry)) {
          await sleep(backoffMs(attempt, retry, random))
          continue
        }
        throw error
      }

      const parsed = spec.schema.safeParse(envelope.data)
      if (!parsed.success) {
        // ⚠ 成功壳但 data 对不上契约 = 契约漂移。必须显眼，不能当成普通失败吞掉
        throw new ApiError({
          code: CLIENT_CONTRACT_DRIFT,
          message: `${spec.method} ${spec.path} 的 data 与契约 schema 不符：${parsed.error.message}`,
          traceId,
          retryable: false,
          httpStatus: response.status,
          cause: parsed.error,
        })
      }
      return parsed.data
    }
  }

  return { baseUrl, tokenStore: options.tokenStore, fetchImpl, request, authHeaders, buildUrl }
}

function parseEnvelope(raw: string): ResultEnvelope | null {
  if (!raw) return null
  let json: unknown
  try {
    json = JSON.parse(raw)
  } catch {
    return null
  }
  const parsed = resultEnvelopeSchema.safeParse(json)
  return parsed.success ? parsed.data : null
}

function toTransportError(
  cause: unknown,
  userSignal: AbortSignal | undefined,
  timedOut: boolean,
): ApiError {
  if (userSignal?.aborted) {
    // 用户主动取消：不是错误，界面不该弹提示
    return new ApiError({
      code: CLIENT_ABORTED,
      message: '请求已被取消',
      traceId: '',
      retryable: false,
      cause,
    })
  }
  if (timedOut) {
    return new ApiError({
      code: CLIENT_TIMEOUT,
      message: '请求超时',
      traceId: '',
      retryable: true,
      cause,
    })
  }
  return new ApiError({
    code: CLIENT_NETWORK,
    message: '网络不可用',
    traceId: '',
    retryable: true,
    cause,
  })
}

function canRetry<S extends z.ZodType>(
  spec: RequestSpec<S>,
  error: ApiError,
  attempt: number,
  policy: RetryPolicy,
): boolean {
  if (attempt >= policy.maxAttempts) return false
  // 判据只看服务端给的 retryable(契约 §2.1)。⚠ 前端不维护码清单 —— 后端明确要求，
  // 而且自己维护的那张表漏一行在正常流量下看不出来，只有上游真抖动时才暴露成重试风暴。
  if (!shouldRetry(error.retryable)) return false
  // ⚠ 写操作没有幂等键就不重试：请求可能已经到了服务端，重发会造成重复副作用
  if (spec.method !== 'GET' && !spec.idempotencyKey) return false
  return true
}

/** 指数退避 + 抖动。抖动是为了避免一批客户端在同一毫秒一起回来把上游再压垮。 */
function backoffMs(attempt: number, policy: RetryPolicy, random: () => number): number {
  const exponential = Math.min(policy.maxDelayMs, policy.baseDelayMs * 2 ** (attempt - 1))
  return Math.round(exponential * (0.5 + random() * 0.5))
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

interface LinkedAbort {
  signal: AbortSignal
  timedOut: () => boolean
  dispose: () => void
}

/**
 * 把「调用方的 signal」和「超时」合成一个。
 *
 * ⚠ 不用 `AbortSignal.any()` —— 构建目标是 safari16，而它要 Safari 17.4 才有。
 *   在 WKWebView 里缺一个方法的表现是整条请求链路静默失败。
 */
function linkAbort(parent: AbortSignal | undefined, timeoutMs: number): LinkedAbort {
  const controller = new AbortController()
  let didTimeout = false

  const timer = setTimeout(() => {
    didTimeout = true
    controller.abort()
  }, timeoutMs)

  const onParentAbort = () => {
    controller.abort()
  }
  if (parent) {
    if (parent.aborted) controller.abort()
    else parent.addEventListener('abort', onParentAbort, { once: true })
  }

  return {
    signal: controller.signal,
    timedOut: () => didTimeout,
    dispose() {
      clearTimeout(timer)
      parent?.removeEventListener('abort', onParentAbort)
    },
  }
}
